/**
 * Merged recipient invitation inbox for the sidebar panel.
 *
 * Button and panel share one fetch (bucket/sort/page + counts) via a provider
 * nested under the sidebar list-view context.
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  INVITE_INBOX_PAGE_SIZE,
  createApiClient,
  type InviteInboxBucket,
  type InviteInboxCounts,
  type InviteInboxItem,
  type InviteInboxSort,
  type InviteInboxSortDir,
} from '@adieuu/shared';
import { useAppConfig } from '../config';
import { useIdentity } from './useIdentity';
import { onSpaceInvitesChanged } from '../services/spacesMembershipEvents';
import { inviteInboxTypesForListView } from '../app/sidebar/invitationNotice';
import { onRecipientInvitesChanged } from '../app/sidebar/invitationInboxEvents';
import { useSidebarListView } from '../app/sidebar/sidebarListView';

const EMPTY_COUNTS: InviteInboxCounts = {
  pending: 0,
  declined: 0,
  expired: 0,
  accepted: 0,
  pendingSpace: 0,
  pendingGroup: 0,
};

export interface InvitationInboxValue {
  bucket: InviteInboxBucket;
  sort: InviteInboxSort;
  dir: InviteInboxSortDir;
  page: number;
  items: InviteInboxItem[];
  total: number;
  totalPages: number;
  counts: InviteInboxCounts;
  loading: boolean;
  selectBucket: (next: InviteInboxBucket) => void;
  selectSort: (next: InviteInboxSort) => void;
  selectDir: (next: InviteInboxSortDir) => void;
  setPage: (page: number) => void;
  refresh: () => Promise<void>;
}

const InvitationInboxContext = createContext<InvitationInboxValue | null>(null);

function useInvitationInboxState(): InvitationInboxValue {
  const { apiBaseUrl } = useAppConfig();
  const api = useMemo(() => createApiClient({ baseUrl: apiBaseUrl }), [apiBaseUrl]);
  const { status: identityStatus } = useIdentity();
  const { listView } = useSidebarListView();

  const [bucket, setBucket] = useState<InviteInboxBucket>('pending');
  const [sort, setSort] = useState<InviteInboxSort>('createdAt');
  const [dir, setDir] = useState<InviteInboxSortDir>('desc');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<InviteInboxItem[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<InviteInboxCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(false);

  const isLoggedIn = identityStatus === 'logged_in';
  const types = useMemo(() => inviteInboxTypesForListView(listView), [listView]);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setItems([]);
      setTotal(0);
      setCounts(EMPTY_COUNTS);
      return;
    }
    setLoading(true);
    const res = await api.invites.listInbox({
      bucket,
      sort,
      dir,
      limit: INVITE_INBOX_PAGE_SIZE,
      offset: (page - 1) * INVITE_INBOX_PAGE_SIZE,
      types,
    });
    if (res.success && res.data) {
      setItems(res.data.items);
      setTotal(res.data.total);
      setCounts(res.data.counts);
    } else {
      setItems([]);
      setTotal(0);
      setCounts(EMPTY_COUNTS);
    }
    setLoading(false);
  }, [api, bucket, dir, isLoggedIn, page, sort, types]);

  useEffect(() => {
    setPage(1);
  }, [listView]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => onSpaceInvitesChanged(() => void refresh()), [refresh]);
  useEffect(() => onRecipientInvitesChanged(() => void refresh()), [refresh]);

  const selectBucket = useCallback((next: InviteInboxBucket) => {
    setBucket(next);
    setPage(1);
  }, []);

  const selectSort = useCallback((next: InviteInboxSort) => {
    setSort(next);
    setPage(1);
  }, []);

  const selectDir = useCallback((next: InviteInboxSortDir) => {
    setDir(next);
    setPage(1);
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / INVITE_INBOX_PAGE_SIZE));

  return {
    bucket,
    sort,
    dir,
    page,
    items,
    total,
    totalPages,
    counts,
    loading,
    selectBucket,
    selectSort,
    selectDir,
    setPage,
    refresh,
  };
}

export function InvitationInboxProvider({ children }: { children: ReactNode }) {
  const value = useInvitationInboxState();
  return createElement(InvitationInboxContext.Provider, { value }, children);
}

export function useInvitationInbox(): InvitationInboxValue {
  const ctx = useContext(InvitationInboxContext);
  if (!ctx) {
    throw new Error('useInvitationInbox must be used within an InvitationInboxProvider');
  }
  return ctx;
}
